"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  ChevronDown,
  Rocket,
  Target,
  Bot,
  MessageSquareText,
  PhoneOutgoing,
  Send,
  Voicemail,
  MessagesSquare,
  Phone,
  Users,
  TrendingUp,
  Hash,
  BarChart3,
  Zap,
  Settings,
  CreditCard,
  ShieldCheck,
  Lightbulb,
  HelpCircle,
  FileText,
  PhoneIncoming,
  LifeBuoy,
  Inbox,
  type LucideIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { docsNav } from "@/lib/docs/docsNav";
import type { NavSection } from "@/lib/docs/types";

const iconMap: Record<string, LucideIcon> = {
  Rocket, Target, Bot, MessageSquareText, PhoneOutgoing, Send,
  Voicemail, MessagesSquare, Phone, Users, TrendingUp, Hash,
  BarChart3, Zap, Settings, CreditCard, ShieldCheck, Lightbulb,
  HelpCircle, FileText, PhoneIncoming, LifeBuoy, Inbox,
};

function SidebarSection({ section }: { section: NavSection }) {
  const pathname = usePathname();
  const isActive = pathname.startsWith(`/docs/${section.basePath}`);
  const [isOpen, setIsOpen] = useState(isActive);

  const IconComponent = iconMap[section.icon] ?? FileText;

  return (
    <div className="mb-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md transition-colors",
          "hover:bg-accent hover:text-accent-foreground",
          isActive && !isOpen && "bg-accent/50"
        )}
      >
        <IconComponent className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left font-medium">{section.title}</span>
        <ChevronDown
          className={cn(
            "h-3 w-3 shrink-0 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen && (
        <div className="ml-4 pl-3 border-l mt-1 space-y-0.5">
          {/* Section index link */}
          <Link
            href={`/docs/${section.basePath}`}
            className={cn(
              "block px-2 py-1.5 text-sm rounded-md transition-colors",
              "hover:bg-accent hover:text-accent-foreground",
              pathname === `/docs/${section.basePath}`
                ? "bg-accent text-accent-foreground font-medium"
                : "text-muted-foreground"
            )}
          >
            Overview
          </Link>

          {section.items.map((item) => {
            const href = `/docs/${section.basePath}/${item.slug}`;
            const active = pathname === href;
            return (
              <Link
                key={item.slug}
                href={href}
                className={cn(
                  "flex items-center gap-1.5 px-2 py-1.5 text-sm rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground",
                  active
                    ? "bg-accent text-accent-foreground font-medium"
                    : "text-muted-foreground"
                )}
              >
                <span className="flex-1">{item.title}</span>
                {item.tier && (
                  <Badge
                    variant="outline"
                    className="text-3xs px-1 py-0 h-4 font-normal"
                  >
                    {item.tier === "growth" ? "Growth+" : "Scale"}
                  </Badge>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function DocsSidebar() {
  return (
    <ScrollArea className="h-full">
      <div className="py-4 px-2">
        {docsNav.map((section) => (
          <SidebarSection key={section.basePath} section={section} />
        ))}

        {/* FAQ and Glossary links at the bottom */}
        <div className="mt-2 px-3 space-y-0.5">
          <Link
            href="/docs/faq"
            className="flex items-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <HelpCircle className="h-4 w-4" />
            FAQ
          </Link>
          <Link
            href="/docs/glossary"
            className="flex items-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <FileText className="h-4 w-4" />
            Glossary
          </Link>
        </div>
      </div>
    </ScrollArea>
  );
}
